import React, { useState, useRef } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import classNames from "classnames";
import states from "states-us";

import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { BUSINESS_TYPES, CSU_CERTIFICATIONS } from "../utils/common";
import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidName,
    isValidPhoneNumber,
} from "../utils/utils";

const CSUReferralForm = () => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [relationship, setRelationship] = useState("");
    const [referralFirstName, setReferralFirstName] = useState("");
    const [referralLastName, setReferralLastName] = useState("");
    const [referralPhone, setReferralPhone] = useState("");
    const [referralEmail, setReferralEmail] = useState("");
    const [referralCity, setReferralCity] = useState("");
    const [referralState, setReferralState] = useState("");
    const [referralPositionType, setReferralPositionType] = useState("");
    const [referralCertification, setReferralCertification] = useState("");

    //form state
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updatePhoneNumber = e => {
        let number = getFormattedPhoneNumber(e.target.value);
        setReferralPhone(number);
        setValidity("phoneNumber", isValidPhoneNumber(number));
    };

    const isValid = () => {
        const _errors = {
            firstName: !isValidName(firstName),
            lastName: !isValidName(lastName),
            email: !isValidEmail(email),
            relationship: relationship && !isValidName(relationship),
            referralFirstName: !isValidName(referralFirstName),
            referralLastName: !isValidName(referralLastName),
            referralPhone: !isValidPhoneNumber(referralPhone),
            referralEmail: !isValidEmail(referralEmail),
            referralCity: !isValidName(referralCity),
            referralState: !referralState,
            referralPositionType: !referralPositionType,
            referralCertification: !referralCertification,
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { csuReferral: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            first_name: firstName,
            last_name: lastName,
            email,
            relationship,
            referral_first_name: referralFirstName,
            referral_last_name: referralLastName,
            referral_phone: referralPhone,
            referral_email: referralEmail,
            referral_city: referralCity,
            referral_state: referralState,
            referral_position_type: referralPositionType,
            referral_certification: referralCertification,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        try {
            const token = await reCAPTCHARef.current.executeAsync();
            submitForm(token);
        } catch (error) {
            console.error(error);
        }
    };

    if (submitted && !submissionError) {
        return (
            <div className="success-sumitted-copy">
                <h2 className="mb-4">Referral Form</h2>
                <hr />
                <h3>Thank you!</h3>
                <p>
                    Your referral is one step closer to saving a life. We'll be
                    in touch soon!
                </p>
            </div>
        );
    }

    return (
        <Form
            className={classNames("form csu-referral-form", {
                "has-submit-attempt": hasSubmitAttempt,
            })}
            onSubmit={executeReCAPTCHA}
            key="csu-referral-form"
        >
            <h2 className="mb-4">Referral Form</h2>

            <fieldset>
                <legend className="mb-3 h5">Your Information</legend>
                <Row>
                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="text"
                            placeholder="First Name*"
                            size="lg"
                            required
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            className={classNames({
                                invalid: !!errors["firstName"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="text"
                            placeholder="Last Name*"
                            size="lg"
                            required
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            className={classNames({
                                invalid: !!errors["lastName"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="email"
                            placeholder="Email*"
                            size="lg"
                            required
                            value={email}
                            onChange={e => {
                                setEmail(e.target.value);
                                setValidity(
                                    "email",
                                    isValidEmail(e.target.value)
                                );
                            }}
                            className={classNames({
                                invalid: !!errors["email"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="text"
                            placeholder="Relationship to Referral"
                            size="lg"
                            value={relationship}
                            onChange={e => setRelationship(e.target.value)}
                            className={classNames({
                                invalid: !!errors["relationship"],
                            })}
                        />
                    </Col>
                </Row>
            </fieldset>

            <hr />

            <fieldset>
                <legend className="mb-3 h5">Your Referral</legend>
                <Row>
                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="text"
                            placeholder="Referral First Name*"
                            size="lg"
                            value={referralFirstName}
                            onChange={e => setReferralFirstName(e.target.value)}
                            className={classNames({
                                invalid: !!errors["referralFirstName"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="text"
                            placeholder="Referral Last Name*"
                            size="lg"
                            value={referralLastName}
                            onChange={e => setReferralLastName(e.target.value)}
                            className={classNames({
                                invalid: !!errors["referralLastName"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="tel"
                            maxLength="14"
                            placeholder="Phone Number*"
                            size="lg"
                            value={referralPhone}
                            onChange={updatePhoneNumber}
                            className={classNames({
                                invalid: !!errors["referralPhone"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="email"
                            placeholder="Email*"
                            size="lg"
                            required
                            value={referralEmail}
                            onChange={e => {
                                setReferralEmail(e.target.value);
                                setValidity(
                                    "referralEmail",
                                    isValidEmail(e.target.value)
                                );
                            }}
                            className={classNames({
                                invalid: !!errors["referralEmail"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Control
                            type="text"
                            placeholder="City*"
                            size="lg"
                            value={referralCity}
                            onChange={e => setReferralCity(e.target.value)}
                            className={classNames({
                                invalid: !!errors["referralCity"],
                            })}
                        />
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Select
                            required
                            size="lg"
                            defaultValue={referralState}
                            onChange={e => setReferralState(e.target.value)}
                            className={classNames({
                                "select-placeholder": !referralState,
                                invalid: !!errors["referralState"],
                            })}
                        >
                            <option disabled value="">
                                State/Region*
                            </option>
                            {states.map(state => (
                                <option
                                    value={state.abbreviation}
                                    key={state.abbreviation}
                                >
                                    {state.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Select
                            required
                            size="lg"
                            defaultValue={referralPositionType}
                            onChange={e =>
                                setReferralPositionType(e.target.value)
                            }
                            className={classNames({
                                "select-placeholder": !referralPositionType,
                                invalid: !!errors["referralPositionType"],
                            })}
                        >
                            <option disabled value="">
                                Position Type*
                            </option>
                            {BUSINESS_TYPES.map(business => (
                                <option value={business} key={business}>
                                    {business}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={6} className="mb-4">
                        <Form.Select
                            required
                            size="lg"
                            defaultValue={referralCertification}
                            onChange={e =>
                                setReferralCertification(e.target.value)
                            }
                            className={classNames({
                                "select-placeholder": !referralCertification,
                                invalid: !!errors["referralCertification"],
                            })}
                        >
                            <option disabled value="">
                                Certification*
                            </option>
                            {CSU_CERTIFICATIONS.map(business => (
                                <option value={business} key={business}>
                                    {business}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
            </fieldset>

            {submissionError && (
                <Alert variant="danger">{submissionError}</Alert>
            )}

            <Button
                className="mt-4"
                type="submit"
                size="lg"
                disabled={submitting}
            >
                Submit
            </Button>

            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>
    );
};

export default CSUReferralForm;
